import type { AiManagedGroupDiagnostics } from "@adpilot/shared";
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconCircleX,
  IconHelpCircle,
  IconRefresh,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AnimatedNumber } from "@/components/ui/animated-number";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/preview-card";
import { Skeleton } from "@/components/ui/skeleton";
import { useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";

interface ManagedGroupDiagnosticsPanelProps {
  diagnostics?: AiManagedGroupDiagnostics;
  isLoading: boolean;
  isError: boolean;
  compact?: boolean;
  onApplyRecommendation?: (targetAcos: number) => void;
  onRetry: () => void;
}

const CLASSIFICATION_STYLE = {
  blocked: {
    icon: IconCircleX,
    variant: "destructive" as const,
  },
  attention: {
    icon: IconAlertTriangle,
    variant: "warning" as const,
  },
  suitable: {
    icon: IconCircleCheck,
    variant: "success" as const,
  },
};

export function ManagedGroupDiagnosticsPanel({
  diagnostics,
  isLoading,
  isError,
  compact = false,
  onApplyRecommendation,
  onRetry,
}: ManagedGroupDiagnosticsPanelProps): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  const { formatNumber, formatPercent } = useFormatters();

  if (isLoading && !diagnostics) {
    return (
      <section aria-label={t("diagnostics.title")} className="space-y-3">
        <Skeleton className="h-5 w-36" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-28 w-full" />
      </section>
    );
  }

  if (isError || !diagnostics) {
    return (
      <Alert variant="warning">
        <IconAlertTriangle />
        <AlertTitle>{t("diagnostics.unavailable")}</AlertTitle>
        <AlertDescription className="flex items-end justify-between gap-3">
          <span>{t("diagnostics.unavailableDescription")}</span>
          <Button size="xs" variant="outline" onClick={onRetry}>
            <IconRefresh data-icon="inline-start" />
            {t("diagnostics.retry")}
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  const style = CLASSIFICATION_STYLE[diagnostics.classification];
  const StatusIcon = style.icon;
  const recommendation = diagnostics.recommendation;
  const adjustmentPercent = recommendation
    ? Math.abs(recommendation.businessGoalAdjustment.factor - 1)
    : 0;
  if (compact) {
    const hasConversionSignal =
      diagnostics.metrics.orders > 0 && diagnostics.metrics.actualAcos !== null;
    return (
      <section aria-label={t("diagnostics.title")} className="flex min-w-0 flex-col gap-[9px]">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-[7px]">
            <StatusIcon
              aria-hidden
              width={14}
              height={14}
              viewBox="0 0 24 24"
              strokeWidth={1.8}
              className={cn(
                diagnostics.classification === "suitable"
                  ? "text-badge-success-foreground"
                  : diagnostics.classification === "attention"
                    ? "text-badge-warning-foreground"
                    : "text-destructive-foreground",
              )}
            />
            <h3 className="text-[11px] leading-[14px] font-semibold">{t("diagnostics.title")}</h3>
          </div>
          <DiagnosticsClassificationBadge
            diagnostics={diagnostics}
            showIcon={false}
            size="default"
          />
        </div>

        <div className="flex min-h-[94px] gap-2">
          <dl className="flex w-56 shrink-0 flex-col gap-2 rounded-[9px] border border-line p-[11px]">
            <ReviewMetric
              label={t("diagnostics.dataWindow")}
              value={`${diagnostics.window.coveredDays} / ${diagnostics.window.totalDays} ${t("diagnostics.days")}`}
            />
            <ReviewMetric
              label={t("diagnostics.weightedActualAcos")}
              value={
                diagnostics.metrics.actualAcos === null
                  ? "—"
                  : formatPercent(diagnostics.metrics.actualAcos)
              }
            />
            <ReviewMetric
              label={t("diagnostics.orders")}
              value={formatNumber(diagnostics.metrics.orders)}
            />
          </dl>

          <div className="flex min-w-0 flex-1 flex-col overflow-hidden rounded-[9px] border border-line">
            {hasConversionSignal ? (
              <ReviewSignal
                tone="success"
                title={t("diagnostics.conversionSignal")}
                description={t("diagnostics.conversionSignalDescription")}
              />
            ) : null}
            {diagnostics.reasons.map((item) => {
              const isBudgetAttention =
                item.code === "budget_near_exhaustion" || item.code === "budget_limited";
              const hasAcosRange =
                item.code === "acos_spread_high" &&
                diagnostics.summary.acosMin !== null &&
                diagnostics.summary.acosMax !== null;
              const title =
                isBudgetAttention || item.code === "acos_spread_high"
                  ? t(`diagnostics.reasonTitles.${item.code}`, {
                      count: item.campaignIds.length,
                    })
                  : t(`diagnostics.reasons.${item.code}`, {
                      count: item.campaignIds.length,
                    });
              const description = hasAcosRange
                ? t("diagnostics.reasonDescriptions.acos_spread_high", {
                    min: formatPercent(diagnostics.summary.acosMin ?? 0),
                    max: formatPercent(diagnostics.summary.acosMax ?? 0),
                  })
                : isBudgetAttention
                  ? t("diagnostics.reasonDescriptions.budgetEstimate")
                  : undefined;
              return (
                <ReviewSignal
                  key={item.code}
                  tone={item.severity === "blocked" ? "destructive" : "warning"}
                  title={title}
                  description={description}
                />
              );
            })}
            {!hasConversionSignal && diagnostics.reasons.length === 0 ? (
              <ReviewSignal tone="success" title={t("diagnostics.noConcerns")} />
            ) : null}
          </div>
        </div>
      </section>
    );
  }
  return (
    <section aria-label={t("diagnostics.title")} className="flex min-w-0 flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h3 className="text-[11px] leading-[14px] font-semibold">{t("diagnostics.title")}</h3>
          <p className="mt-0.5 text-[9px] leading-3 text-muted-foreground">
            {t("diagnostics.window", {
              covered: diagnostics.window.coveredDays,
              total: diagnostics.window.totalDays,
            })}
          </p>
        </div>
        <DiagnosticsClassificationBadge diagnostics={diagnostics} size="compact" />
      </div>

      <div
        data-slot="diagnostics-metrics-card"
        className="rounded-[10px] border border-line bg-surface p-3"
      >
        <div className="grid grid-cols-3 gap-2">
          <DiagnosticMetric
            label={t("diagnostics.actualAcos")}
            value={
              diagnostics.metrics.actualAcos === null
                ? "—"
                : formatPercent(diagnostics.metrics.actualAcos)
            }
          />
          <DiagnosticMetric
            label={t("diagnostics.orders")}
            value={formatNumber(diagnostics.metrics.orders)}
          />
          <DiagnosticMetric
            label={t("diagnostics.dataThrough")}
            value={diagnostics.window.dataThrough ?? "—"}
          />
        </div>
      </div>

      {recommendation ? (
        <div
          data-slot="diagnostics-recommendation-card"
          className="rounded-[10px] border border-line bg-subtle/45 p-3"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-1">
                <p className="text-[9px] leading-3 text-muted-foreground">
                  {t("diagnostics.recommendedTargetAcos")}
                </p>
                <HoverCard>
                  <HoverCardTrigger
                    delay={150}
                    closeDelay={100}
                    render={
                      <button
                        type="button"
                        aria-label={t("diagnostics.formulaAriaLabel")}
                        className="inline-flex size-4 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-control-highlight hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      />
                    }
                  >
                    <IconHelpCircle
                      aria-hidden
                      width={13}
                      height={13}
                      viewBox="0 0 24 24"
                      strokeWidth={1.8}
                    />
                  </HoverCardTrigger>
                  <HoverCardContent align="start" className="w-[300px] flex-col p-3">
                    <p className="text-[10px] leading-[15px] font-semibold">
                      {t("diagnostics.formulaTitle")}
                    </p>
                    <p className="mt-1 text-[9px] leading-[14px] text-muted-foreground">
                      {t("diagnostics.recommendationFormula", {
                        goal: t(
                          `policy.goals.${recommendation.businessGoalAdjustment.businessGoal}`,
                        ),
                        baseline: formatPercent(recommendation.baselineAcos),
                        factor: formatNumber(recommendation.businessGoalAdjustment.factor, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }),
                        direction: t(
                          `diagnostics.adjustmentDirections.${recommendation.businessGoalAdjustment.direction}`,
                          { adjustment: formatPercent(adjustmentPercent) },
                        ),
                        target: formatPercent(recommendation.targetAcos),
                      })}
                    </p>
                  </HoverCardContent>
                </HoverCard>
              </div>
              <AnimatedNumber
                value={recommendation.targetAcos}
                startOnView={false}
                format={formatPercent}
                className="mt-0.5 block font-mono text-lg leading-6 font-semibold"
              />
            </div>
            {onApplyRecommendation ? (
              <Button
                size="xs"
                variant="outline"
                onClick={() => onApplyRecommendation(recommendation.targetAcos)}
              >
                {t("diagnostics.apply")}
              </Button>
            ) : null}
          </div>
          <p className="mt-2 text-[9px] leading-[14px] text-muted-foreground">
            {t("diagnostics.recommendationBasis", {
              min: formatPercent(recommendation.rangeMin),
              max: formatPercent(recommendation.rangeMax),
              days: recommendation.basedOnCoveredDays,
            })}
          </p>
        </div>
      ) : (
        <p className="rounded-[8px] bg-subtle px-3 py-2 text-[9px] leading-[14px] text-muted-foreground">
          {t("diagnostics.noRecommendation")}
        </p>
      )}

      {diagnostics.reasons.length > 0 ? (
        <div className="flex flex-col gap-2">
          {diagnostics.reasons.map((item) => (
            <div key={item.code} className="flex gap-2 text-[9px] leading-[14px]">
              <span
                className={cn(
                  "mt-1 size-1.5 shrink-0 rounded-full",
                  item.severity === "blocked" ? "bg-destructive" : "bg-warning",
                )}
              />
              <span className="text-muted-foreground">
                {t(`diagnostics.reasons.${item.code}`, { count: item.campaignIds.length })}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[9px] leading-[14px] text-muted-foreground">
          {t("diagnostics.noConcerns")}
        </p>
      )}

      {!compact ? (
        <p className="border-t border-line pt-2 text-[9px] leading-[14px] text-muted-foreground">
          {t("diagnostics.disclaimer")}
        </p>
      ) : null}
    </section>
  );
}

function DiagnosticsClassificationBadge({
  diagnostics,
  showIcon = true,
  size,
}: {
  diagnostics: AiManagedGroupDiagnostics;
  showIcon?: boolean;
  size: "compact" | "default";
}): React.ReactElement {
  const { t } = useTranslation("managedGroups");
  const style = CLASSIFICATION_STYLE[diagnostics.classification];
  const StatusIcon = style.icon;
  const status = t(`diagnostics.classification.${diagnostics.classification}`);

  return (
    <HoverCard>
      <HoverCardTrigger
        delay={150}
        closeDelay={100}
        render={
          <Badge
            render={<button type="button" />}
            aria-label={t("diagnostics.classificationAriaLabel", { status })}
            className="cursor-help"
            variant={style.variant}
            size={size}
          />
        }
      >
        {showIcon ? <StatusIcon data-icon="inline-start" /> : null}
        {status}
      </HoverCardTrigger>
      <HoverCardContent align="end" className="w-[300px] flex-col p-3 text-left">
        <p className="text-[10px] leading-[15px] font-semibold">{status}</p>
        <p className="mt-1 text-[9px] leading-[14px] text-muted-foreground">
          {t(`diagnostics.classificationDescriptions.${diagnostics.classification}`)}
        </p>
        <div className="mt-2 border-t border-line pt-2">
          {diagnostics.reasons.length > 0 ? (
            <ul className="flex flex-col gap-1.5">
              {diagnostics.reasons.map((reason) => (
                <li key={reason.code} className="flex gap-2 text-[9px] leading-[14px]">
                  <span
                    className={cn(
                      "mt-1 size-1.5 shrink-0 rounded-full",
                      reason.severity === "blocked" ? "bg-destructive" : "bg-warning",
                    )}
                  />
                  <span>
                    {t(`diagnostics.reasons.${reason.code}`, {
                      count: reason.campaignIds.length,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[9px] leading-[14px]">{t("diagnostics.noConcerns")}</p>
          )}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

function DiagnosticMetric({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="min-w-0">
      <p className="truncate text-[8px] leading-[10px] text-muted-foreground uppercase">{label}</p>
      <p className="mt-1 truncate font-mono text-[10px] leading-[14px] font-semibold tabular-nums">
        {value}
      </p>
    </div>
  );
}

function ReviewMetric({ label, value }: { label: string; value: string }): React.ReactElement {
  return (
    <div className="flex items-center justify-between gap-2 text-[9px] leading-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-mono font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

function ReviewSignal({
  description,
  title,
  tone,
}: {
  description?: string;
  title: string;
  tone: "destructive" | "success" | "warning";
}): React.ReactElement {
  const SignalIcon = tone === "success" ? IconCircleCheck : IconAlertTriangle;
  return (
    <div className="flex min-h-[46px] items-start gap-2 border-b border-line px-[11px] py-2.5 last:border-b-0">
      <SignalIcon
        aria-hidden
        width={14}
        height={14}
        viewBox="0 0 24 24"
        strokeWidth={1.8}
        className={cn(
          "shrink-0",
          tone === "success"
            ? "text-badge-success-foreground"
            : tone === "warning"
              ? "text-badge-warning-foreground"
              : "text-destructive-foreground",
        )}
      />
      <div className="flex min-w-0 flex-col gap-px">
        <p className="text-[9px] leading-3 font-semibold">{title}</p>
        {description ? (
          <p className="text-[8px] leading-[11px] text-muted-foreground">{description}</p>
        ) : null}
      </div>
    </div>
  );
}
